/**
 * IntlBoot — single <IntlProvider> instance whose locale + messages props
 * change in place when the user toggles language. Re-mounting the provider
 * would clear the Query cache, lose form input state, etc. — the
 * single-instance pattern preserves all sibling state (RESEARCH § 10).
 *
 * Sets document.documentElement.dir + lang on every locale change. Tailwind
 * v4.2 logical-property utilities flip layout based on <html dir>.
 */
import { useEffect } from "react";
import { IntlProvider } from "react-intl";
import type { ReactNode } from "react";

import enMessages from "./messages.en.json";
import arMessages from "./messages.ar.json";
import { useLocale } from "./useLocale";

const MESSAGES: Record<string, Record<string, string>> = {
  en: enMessages as Record<string, string>,
  ar: arMessages as Record<string, string>,
};

interface IntlBootProps {
  children: ReactNode;
}

export function IntlBoot({ children }: IntlBootProps) {
  const stored = useLocale();
  // Default to "en" before /picker choice so first-paint doesn't crash.
  const locale = stored ?? "en";

  useEffect(() => {
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = locale;
  }, [locale]);

  // R5-003 (audit 2026-07) — V-CP4 Latin-digit lock for react-intl message
  // interpolations ({count} etc.), which format through Intl.NumberFormat
  // internally where the ESLint constructor rule cannot see them. Bare "ar"
  // happens to resolve to 'latn' by CLDR accident; "ar-SA" — this
  // municipality's region, and the natural refinement — resolves to 'arab'
  // and would flip every count to Arabic-Indic digits. The -u-nu-latn
  // Unicode extension pins Latin digits for any current or future
  // ar-prefixed tag. Rendered output today is byte-identical.
  const intlLocale = locale.startsWith("ar") ? `${locale}-u-nu-latn` : locale;

  return (
    <IntlProvider locale={intlLocale} messages={MESSAGES[locale]} defaultLocale="en">
      {children}
    </IntlProvider>
  );
}
